<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { onKeyStroke } from '@vueuse/core';

import { Landing } from '@libs/trip-planner-interfaces';
import { performanceMark } from '@libs/utils';

import ShareWidgetInit from '../../core/components/ShareWidgetInit.vue';
import TheHeader from '../../core/components/theHeader/TheHeader.vue';
import {
  useBrowserStore,
  useRouterStore,
  useStatsigEventsStore,
} from '../../core/stores';
import { AutoCompleteType } from '../../modules/autoComplete';
import { useMapStore } from '../../core/stores/mapStore/mapStore';
import { useRecipeStore } from '../../core/stores/recipe/recipeStore';

import TripOverviewPageLayout from './TripOverviewPageLayout.vue';
import BuildYourTripLayout from './components/BuildYourTripLayout.vue';
import TripOverviewColumn from './components/tripItinerary/TripOverviewColumn.vue';
import BuildYourTripPanel from './components/BuildYourTripPanel.vue';
import TripOverviewModals from './components/TripOverviewModals.vue';
import TripOverviewPanels from './components/TripOverviewPanels.vue';
import TripOverviewMap from './components/TripOverviewMap.vue';
import TripOverviewNotifications from './components/TripOverviewNotifications.vue';
import {
  usePremadeTripStore,
  useTripOverviewPageStore,
  useTripOverviewModalsStore,
  useTripOverviewPanelsStore,
  useTripOverviewNotificationsStore,
  useTripSummaryStore,
} from './stores';
import TripSummaryColumn from './components/tripSummary/TripSummaryColumn.vue';
import TripPriceSummaryFooterMobile from './components/tripSummary/TripPriceSummaryFooterMobile.vue';
import { TripOverviewPanelType } from './models';

const tripOverviewPageStore = useTripOverviewPageStore();
const mapStore = useMapStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const tripOverviewNotificationsStore = useTripOverviewNotificationsStore();
const tripSummaryStore = useTripSummaryStore();
const premadeTripStore = usePremadeTripStore();
const routerStore = useRouterStore();
const browserStore = useBrowserStore();
const statsigEventsStore = useStatsigEventsStore();
const recipeStore = useRecipeStore();

const moblieFooterHeight = '80px';
const isLongScroll = ref<boolean>(false);
const tripSummaryColumnRef = ref<InstanceType<typeof TripSummaryColumn> | null>(
  null
);

const shouldShowSummaryMobileFooter = computed(
  () =>
    browserStore.isSmallerThan1200px &&
    tripOverviewPageStore.hasRecipeIngredients &&
    !tripSummaryStore.isTripSummaryVisible &&
    tripOverviewPanelsStore.tripOverviewPanelType ===
      TripOverviewPanelType.None &&
    isLongScroll.value
);

function showInitialView(): void {
  if (!routerStore.isInitialLoad) {
    return;
  }

  if (recipeStore.isSingleHotelRecipe) {
    if (routerStore.landing === Landing.room) {
      tripOverviewPanelsStore.showMetaHotelDetails();
    }
  }

  routerStore.isInitialLoad = false;
}

function scrollToTripSummary(): void {
  const el = tripSummaryColumnRef.value?.$el;
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

onMounted(() => {
  performanceMark('TripOverviewPageRendered');

  showInitialView();
  premadeTripStore.tryToFetchPremadeTripDetails();
  tripOverviewModalsStore.openModalAutomatically();
  tripOverviewPageStore.initializeView();
});

onUnmounted(() => {
  tripOverviewModalsStore.closeTripOverviewModal();
  tripOverviewPanelsStore.closeTripOverviewPanel();
  tripOverviewNotificationsStore.clearNotifications();
});

onKeyStroke('p', (event) => {
  const correctKeyShortcutTriggered = event.ctrlKey || event.metaKey;
  if (
    tripSummaryStore.isEveryIngredientAvailable &&
    correctKeyShortcutTriggered
  ) {
    statsigEventsStore.logTripOverviewPrintModeOpened({
      source: 'shortcut',
    });
    event.preventDefault();
    routerStore.navigateToTripOverviewPrint();
  }
});

function onLongScroll(): void {
  isLongScroll.value = true;
}
</script>

<template>
  <TripOverviewModals />
  <TripOverviewPanels />

  <template v-if="tripOverviewPageStore.hasRecipeIngredients">
    <TripOverviewNotifications />
    <TripOverviewMap
      v-if="mapStore.shouldShowFullScreenMap"
      :should-have-border-radius="false"
      @close="mapStore.closeFullScreenDynamicTripMap()"
    />
    <ShareWidgetInit />

    <TripOverviewPageLayout
      :class="{ 'has-mobile-footer': shouldShowSummaryMobileFooter }"
      @long-scroll="onLongScroll"
    >
      <template #leftColumn>
        <TheHeader
          :should-show-print-button="
            tripSummaryStore.isEveryIngredientAvailable &&
            !browserStore.isSmallerThan1200px
          "
          class="header"
        />
        <TripOverviewColumn />
      </template>
      <template #rightColumn>
        <TripSummaryColumn ref="tripSummaryColumnRef" />
      </template>
    </TripOverviewPageLayout>
  </template>

  <template v-else>
    <BuildYourTripLayout>
      <BuildYourTripPanel
        @build-your-trip="
          tripOverviewModalsStore.openDestinationSelectorModal(
            '',
            undefined,
            AutoCompleteType.Destination
          )
        "
      />
    </BuildYourTripLayout>
  </template>
  <TripPriceSummaryFooterMobile
    v-show="shouldShowSummaryMobileFooter"
    class="trip-summary-mobile-footer"
    @show-summary-clicked="scrollToTripSummary"
  />
</template>

<style lang="scss" scoped>
.header {
  padding: var(--spacing-16) var(--spacing-12);
  background-color: var(--color-white);
}

.has-mobile-footer {
  margin-bottom: v-bind(moblieFooterHeight);
}

.trip-summary-mobile-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: v-bind(moblieFooterHeight);
  background-color: var(--color-white);
}
</style>
